import { Box, Center, Group, Paper, Stack, Text } from '@mantine/core'
import { IconAlertCircle, IconInboxOff } from '@tabler/icons-react'
import { IsaacComplainDetailRes } from '../../typings/complainTypes'

interface ComplaintListProps {
  complaints: IsaacComplainDetailRes[]
}

const formatTime = (time?: string) => {
  if (!time) return '-'
  return time.replace('T', ' ').substring(0, 16)
}

const formatAmount = (amount?: number) => {
  if (!amount && amount !== 0) return '-'
  return `¥${amount.toFixed(2)}`
}

const getProgressConfig = (
  status: number,
  orderStatus?: number
): { bgColor: string; textColor: string; message: string; showComplaintLink: boolean } => {
  switch (status) {
    case 1:
      return {
        bgColor: '#FEF3C7',
        textColor: '#92400E',
        message: '',
        showComplaintLink: false,
      }
    case 2:
      return {
        bgColor: '#DBEAFE',
        textColor: '#1E40AF',
        message: '您的投诉已进入处理流程，我们正在努力核实相关情况。预计48小时内为您反馈结果。',
        showComplaintLink: false,
      }
    case 3:
      if (orderStatus === 100) {
        return {
          bgColor: '#D1FAE5',
          textColor: '#065F46',
          message:
            '您的投诉已处理完毕！款项已退回，请您注意查收！若有疑问请点击联系我们咨询。感谢您的理解与支持，祝您生活愉快！',
          showComplaintLink: true,
        }
      } else {
        return {
          bgColor: '#F3F4F6',
          textColor: '#374151',
          message: '您的投诉已处理完毕。经核实，该问题已处理。如需了解详细处理结果，请联系客服咨询。感谢您的理解！',
          showComplaintLink: true,
        }
      }
    default:
      return {
        bgColor: '#F3F4F6',
        textColor: '#6B7280',
        message: '处理状态未知',
        showComplaintLink: false,
      }
  }
}

const handleComplaintClick = () => {
  window.location.href = 'tel:4000799168'
}

const handleFormClick = () => {
  window.open('https://jsj.yuexiangcha.com/f/FKbq3s', '_blank')
}

interface InfoItemProps {
  label: string
  value: string | number
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <Group gap="0" wrap="nowrap">
    <Text size="sm" c="gray.6" style={{ minWidth: 'fit-content' }}>
      {label}：
    </Text>
    <Text size="sm" c="gray.8" fw={500}>
      {value}
    </Text>
  </Group>
)

export default function ComplaintList({ complaints }: ComplaintListProps) {
  if (complaints.length === 0) {
    return (
      <Paper p="xl" radius="lg" style={{ backgroundColor: '#ffffff' }}>
        <Center py="xl">
          <Stack align="center" gap="md">
            <Box
              style={{
                padding: '16px',
                borderRadius: '50%',
                backgroundColor: '#F3F4F6',
              }}
            >
              <IconInboxOff size={48} color="#9CA3AF" />
            </Box>
            <Stack align="center" gap="xs">
              <Text size="lg" fw={600} c="gray.7">
                暂无投诉记录
              </Text>
              <Text size="sm" c="dimmed">
                您还没有提交过投诉
              </Text>
            </Stack>
          </Stack>
        </Center>
      </Paper>
    )
  }

  return (
    <Stack gap="md">
      {complaints.map((complaint) => {
        const progressConfig = getProgressConfig(complaint.status, complaint.orderStatus)

        return (
          <Paper
            key={complaint.id}
            p="md"
            radius="lg"
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #E5E7EB',
            }}
          >
            <Stack gap="sm">
              <Box
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                <Stack gap="xs">
                  <InfoItem label="报告名称" value={complaint.templateName || '-'} />
                  <InfoItem label="交易金额" value={formatAmount(complaint.payAmount)} />
                  <InfoItem label="交易时间" value={formatTime(complaint.complainTime)} />
                  <InfoItem label="订单编号" value={complaint.orderCode} />
                </Stack>
              </Box>

              {complaint.content && (
                <Box
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                >
                  <Stack gap="xs">
                    <Text size="sm" c="gray.6">
                      投诉内容：
                    </Text>
                    <Text size="sm" c="gray.8" fw={500} style={{ lineHeight: 1.6 }}>
                      {complaint.content}
                    </Text>
                  </Stack>
                </Box>
              )}

              <Box
                style={{
                  backgroundColor: progressConfig.bgColor,
                  borderRadius: '8px',
                  padding: '12px',
                  border: `1px solid ${progressConfig.textColor}20`,
                }}
              >
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconAlertCircle size={16} color={progressConfig.textColor} />
                    <Text size="sm" fw={600} style={{ color: progressConfig.textColor }}>
                      处理进度
                    </Text>
                  </Group>

                  {complaint.status === 1 ? (
                    <Text size="sm" style={{ color: progressConfig.textColor, lineHeight: 1.6 }}>
                      为了更好帮助您解决问题，请您点击{' '}
                      <Text
                        component="span"
                        fw={600}
                        style={{
                          color: '#F59E0B',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={handleFormClick}
                      >
                        此处
                      </Text>{' '}
                      将信息及凭证提交给我方，我们将尽快为您核实处理！
                    </Text>
                  ) : (
                    <Text
                      size="sm"
                      style={{
                        color: progressConfig.textColor,
                        lineHeight: 1.6,
                      }}
                    >
                      {progressConfig.message}
                    </Text>
                  )}

                  {progressConfig.showComplaintLink && (
                    <Group justify="flex-end" mt="xs">
                      <Text
                        size="sm"
                        fw={600}
                        style={{
                          color: '#F59E0B',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={handleComplaintClick}
                      >
                        点击联系我们 →
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        )
      })}
    </Stack>
  )
}
